/**
 * ComparisonTable セクション。3 variants:
 *   standard   — 既存形式 (modelName 行 + columnHeaders 列)
 *   spec_sheet — 縦長スペック表 (1 モデル × 多項目)
 *   matrix     — ○×評価マトリクス (記号セルを色で強調)
 */
import type { ComparisonTableConfig } from '@goenhub/shared';
import { Table2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { TextField } from './primitives';
import type { EditorProps, RendererProps, SectionDefinition } from './types';

interface SpecSheetRow {
  label: string;
  value: string;
}
interface SpecSheetConfig {
  title: string;
  modelName: string;
  rows: SpecSheetRow[];
}

function StandardEditor({ config, onChange, onCommit }: EditorProps) {
  const cfg = config as unknown as ComparisonTableConfig;
  const cols = cfg.columnHeaders ?? [];
  const rows = cfg.rows ?? [];

  const update = (next: ComparisonTableConfig) => {
    onChange(next as unknown as Record<string, unknown>);
  };

  return (
    <div className="space-y-3">
      <TextField
        label="表のタイトル"
        value={cfg.title ?? ''}
        onChange={(v) => update({ ...cfg, title: v })}
        onCommit={onCommit}
      />
      <div className="overflow-x-auto">
        <table className="text-xs border border-[var(--border)] w-full">
          <thead>
            <tr>
              <th className="p-1 border-r border-[var(--border)] whitespace-nowrap">
                モデル / バリエーション
              </th>
              {cols.map((c, i) => (
                <th key={i} className="p-1 border-r border-[var(--border)]">
                  <div className="flex gap-1">
                    <Input
                      value={c}
                      onChange={(e) => {
                        const next = [...cols];
                        next[i] = e.target.value;
                        update({ ...cfg, columnHeaders: next });
                      }}
                      onBlur={onCommit}
                      className="text-xs w-24"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        update({
                          ...cfg,
                          columnHeaders: cols.filter((_, idx) => idx !== i),
                          rows: rows.map((r) => ({
                            ...r,
                            cells: r.cells.filter((_, idx) => idx !== i),
                          })),
                        });
                      }}
                      className="text-red-500"
                      aria-label="列を削除"
                    >
                      ×
                    </button>
                  </div>
                </th>
              ))}
              <th className="p-1">
                <button
                  type="button"
                  onClick={() => {
                    update({
                      ...cfg,
                      columnHeaders: [...cols, `機能${cols.length + 1}`],
                      rows: rows.map((r) => ({ ...r, cells: [...r.cells, '○'] })),
                    });
                  }}
                  className="text-[var(--accent)]"
                >
                  + 列
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} className="border-t border-[var(--border)]">
                <td className="p-1">
                  <Input
                    value={r.modelName}
                    onChange={(e) => {
                      const next = rows.slice();
                      next[ri] = { ...r, modelName: e.target.value };
                      update({ ...cfg, rows: next });
                    }}
                    onBlur={onCommit}
                    placeholder="モデル名"
                    className="text-xs w-24"
                  />
                  <Input
                    value={r.variation ?? ''}
                    onChange={(e) => {
                      const next = rows.slice();
                      next[ri] = { ...r, variation: e.target.value };
                      update({ ...cfg, rows: next });
                    }}
                    onBlur={onCommit}
                    placeholder="バリエーション"
                    className="ml-1 text-xs w-24 mt-1"
                  />
                </td>
                {r.cells.map((cell, ci) => (
                  <td key={ci} className="p-1 border-l border-[var(--border)]">
                    <Input
                      value={cell}
                      onChange={(e) => {
                        const cells = [...r.cells];
                        cells[ci] = e.target.value;
                        const nextRows = rows.slice();
                        nextRows[ri] = { ...r, cells };
                        update({ ...cfg, rows: nextRows });
                      }}
                      onBlur={onCommit}
                      className="text-xs w-16 text-center"
                    />
                  </td>
                ))}
                <td className="p-1">
                  <button
                    type="button"
                    onClick={() => {
                      update({
                        ...cfg,
                        rows: rows.filter((_, idx) => idx !== ri),
                      });
                    }}
                    className="text-red-500"
                    aria-label="行を削除"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => {
          update({
            ...cfg,
            rows: [
              ...rows,
              {
                modelName: `Model ${rows.length + 1}`,
                variation: '',
                cells: cols.map(() => '○'),
              },
            ],
          });
        }}
      >
        + 行を追加
      </Button>
    </div>
  );
}

function StandardTable({ config }: RendererProps) {
  const v = config as unknown as ComparisonTableConfig;
  return (
    <section>
      {v.title && <h3 className="text-xl font-semibold mb-3">{v.title}</h3>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-[var(--border)]">
          <thead className="bg-[var(--surface-subtle)]">
            <tr>
              <th className="p-2 text-left">モデル</th>
              {(v.columnHeaders ?? []).map((c, i) => (
                <th key={i} className="p-2">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(v.rows ?? []).map((r, i) => (
              <tr key={i} className="border-t border-[var(--border)]">
                <td className="p-2 font-medium">
                  {r.modelName}
                  {r.variation && (
                    <span className="ml-1 text-xs text-[var(--text-muted)]">
                      ({r.variation})
                    </span>
                  )}
                </td>
                {r.cells.map((cell, j) => (
                  <td key={j} className="p-2 text-center">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MatrixTable({ config }: RendererProps) {
  const v = config as unknown as ComparisonTableConfig;
  const cellClass = (text: string) => {
    const t = text.trim();
    if (t === '○' || t === '◯' || t.toLowerCase() === 'yes')
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    if (t === '×' || t === '✕' || t.toLowerCase() === 'no')
      return 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    if (t === '△' || t.toLowerCase() === 'partial')
      return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    return '';
  };
  return (
    <section>
      {v.title && <h3 className="text-xl font-semibold mb-3">{v.title}</h3>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-[var(--border)] border-collapse">
          <thead>
            <tr>
              <th className="p-3 text-left bg-[var(--bg-elevated)] border border-[var(--border)]">
                機能 / モデル
              </th>
              {(v.columnHeaders ?? []).map((c, i) => (
                <th
                  key={i}
                  className="p-3 bg-[var(--bg-elevated)] border border-[var(--border)] text-center"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(v.rows ?? []).map((r, i) => (
              <tr key={i}>
                <td className="p-3 font-medium border border-[var(--border)] bg-[var(--bg-elevated)]">
                  {r.modelName}
                  {r.variation && (
                    <span className="ml-1 text-xs text-[var(--text-muted)]">
                      ({r.variation})
                    </span>
                  )}
                </td>
                {r.cells.map((cell, j) => (
                  <td
                    key={j}
                    className={`p-3 text-center text-lg font-bold border border-[var(--border)] ${cellClass(
                      cell,
                    )}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SpecSheetEditor({ config, onChange, onCommit }: EditorProps) {
  const cfg = config as unknown as SpecSheetConfig;
  const rows = cfg.rows ?? [];
  const set = (patch: Partial<SpecSheetConfig>) =>
    onChange({ ...config, ...patch });

  return (
    <div className="space-y-3">
      <TextField
        label="セクションタイトル"
        value={cfg.title ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      <TextField
        label="モデル名"
        value={cfg.modelName ?? ''}
        onChange={(v) => set({ modelName: v })}
        onCommit={onCommit}
      />
      <div className="space-y-1">
        <Label className="text-[11px]">スペック項目</Label>
        <div className="space-y-1">
          {rows.map((row, i) => (
            <div key={i} className="flex gap-1">
              <Input
                value={row.label}
                onChange={(e) => {
                  const next = rows.slice();
                  next[i] = { ...row, label: e.target.value };
                  set({ rows: next });
                }}
                onBlur={onCommit}
                placeholder="項目名 (例: 寸法)"
                className="text-xs flex-1"
              />
              <Input
                value={row.value}
                onChange={(e) => {
                  const next = rows.slice();
                  next[i] = { ...row, value: e.target.value };
                  set({ rows: next });
                }}
                onBlur={onCommit}
                placeholder="値 (例: W120 × H80mm)"
                className="text-xs flex-[2]"
              />
              <button
                type="button"
                onClick={() => set({ rows: rows.filter((_, idx) => idx !== i) })}
                className="text-red-500 px-1"
                aria-label="削除"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => set({ rows: [...rows, { label: '', value: '' }] })}
        >
          + 項目を追加
        </Button>
      </div>
    </div>
  );
}

function SpecSheetTable({ config }: RendererProps) {
  const v = config as unknown as SpecSheetConfig;
  return (
    <section>
      {v.title && <h3 className="text-xl font-semibold mb-3">{v.title}</h3>}
      {v.modelName && (
        <div className="mb-3 text-sm text-[var(--text-secondary)]">
          モデル: <span className="font-semibold">{v.modelName}</span>
        </div>
      )}
      <table className="w-full text-sm border border-[var(--border)]">
        <tbody>
          {(v.rows ?? []).map((r, i) => (
            <tr key={i} className="border-t border-[var(--border)]">
              <th className="p-3 text-left font-medium bg-[var(--bg-elevated)] w-1/3">
                {r.label}
              </th>
              <td className="p-3">{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export const comparisonDefinition: SectionDefinition = {
  type: 'comparison_table',
  label: '比較表',
  icon: Table2,
  description: '機能比較表 / スペックシート / 評価マトリクス。',
  defaultVariant: 'standard',
  useCases: ['all'],
  variants: {
    standard: {
      label: '標準比較表',
      description: '行=モデル、列=機能で値を入れる',
      Renderer: StandardTable,
      Editor: StandardEditor,
      sample: () =>
        ({
          title: '機能比較',
          columnHeaders: ['機能A', '機能B', '機能C'],
          rows: [
            { modelName: 'Standard', variation: '', cells: ['○', '×', '○'] },
            { modelName: 'Pro', variation: '', cells: ['○', '○', '○'] },
          ],
        }) as unknown as Record<string, unknown>,
    },
    matrix: {
      label: '評価マトリクス',
      description: '○×△ を色付きで表示',
      Renderer: MatrixTable,
      Editor: StandardEditor,
      sample: () =>
        ({
          title: '対応機能マトリクス',
          columnHeaders: ['Standard', 'Pro', 'Enterprise'],
          rows: [
            { modelName: '基本機能', variation: '', cells: ['○', '○', '○'] },
            { modelName: 'API 連携', variation: '', cells: ['×', '○', '○'] },
            { modelName: 'SAML SSO', variation: '', cells: ['×', '△', '○'] },
          ],
        }) as unknown as Record<string, unknown>,
    },
    spec_sheet: {
      label: 'スペックシート',
      description: '1 モデル × 縦長の項目別スペック',
      Renderer: SpecSheetTable,
      Editor: SpecSheetEditor,
      sample: () =>
        ({
          title: '製品スペック',
          modelName: 'GH-2000 Standard',
          rows: [
            { label: '寸法', value: 'W120 × D60 × H80 mm' },
            { label: '重量', value: '1.4 kg' },
            { label: '電源', value: 'AC 100V / 50-60Hz' },
            { label: '消費電力', value: '15W' },
            { label: '保証期間', value: '12 ヶ月' },
          ],
        }) as unknown as Record<string, unknown>,
    },
  },
};
